import getFingerprint from '@tabeo/sharpei/utils/getFingerprint'
import { getJWT } from '@tabeo/sharpei/utils/jwt'
import config from 'config'

declare global {
  // eslint-disable-next-line no-var, vars-on-top
  var refreshPromise: Promise<{
    token: string
    refresh: string
  }> | null
}

// Marks a refresh failure that ends the session (no valid refresh token, or
// /refresh rejected it), so the axios path in Auth.js knows to log out.
function sessionExpiredError(message: string) {
  const error = new Error(message) as Error & {
    isExpectedSessionExpiry?: boolean
  }
  error.isExpectedSessionExpiry = true
  return error
}

function clearTokens() {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
}

// Clears the tokens only when the session is over: no valid refresh token, or
// the refresh endpoint rejects it. A dropped connection or a server error keeps
// them, so a network blip does not log the user out.
async function requestNewTokens() {
  const currentRefreshToken = getJWT('refreshToken')

  if (!currentRefreshToken) {
    clearTokens()
    throw sessionExpiredError('token expired')
  }

  const response = await fetch(`${config.API_URL}/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      refresh: currentRefreshToken,
      fingerprint: getFingerprint(),
    }),
  })

  if (!response.ok) {
    // statusText is '' under HTTP/2, so embed the status in the message.
    const message = `refresh failed: ${response.status}`
    if (response.status === 401 || response.status === 403) {
      clearTokens()
      throw sessionExpiredError(message)
    }
    throw new Error(message)
  }

  const { token, refresh } = await response.json()

  localStorage.setItem('token', token)
  localStorage.setItem('refreshToken', refresh)

  return { token, refresh }
}

// Single flight for every client. The promise is stored before it can settle,
// so a failed refresh is never cached.
function refreshAccessToken() {
  if (!globalThis.refreshPromise) {
    globalThis.refreshPromise = requestNewTokens().finally(() => {
      globalThis.refreshPromise = null
    })
  }

  return globalThis.refreshPromise
}

export default refreshAccessToken
